import { Monitor, Moon, Sun } from 'lucide-react';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Light/dark theming.
 *
 * Both themes are deliberately designed — see the two full token sets in `styles/index.css` —
 * rather than one being an inversion of the other. A dark operator console at 3am and a light
 * customer deposit screen at midday are different products with different contrast needs.
 *
 * The preference is stored, and `system` is a real third option rather than an implicit default,
 * so an operator whose OS switches at sunset gets a console that follows.
 */

/** The three theme choices. */
export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'pf.theme';

interface ThemeContextValue {
  /** The stored preference. */
  preference: ThemePreference;
  /** The theme actually applied right now. */
  resolved: 'light' | 'dark';
  /** Change the preference. */
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStored(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* private browsing — fall through to `system` */
  }
  return 'system';
}

/** Provide the theme to the app and keep `<html class="dark">` in sync. */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStored);
  const [systemDark, setSystemDark] = useState(prefersDark);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent): void => {
      setSystemDark(event.matches);
    };
    media.addEventListener('change', listener);
    return () => {
      media.removeEventListener('change', listener);
    };
  }, []);

  const resolved: 'light' | 'dark' =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* preference is session-only in private browsing */
    }
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

/** Read the theme context. */
export function useTheme(): ThemeContextValue {
  const value = use(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>');
  return value;
}

const OPTIONS: { value: ThemePreference; labelKey: string; icon: typeof Sun }[] = [
  { value: 'light', labelKey: 'layout.theme.light', icon: Sun },
  { value: 'dark', labelKey: 'layout.theme.dark', icon: Moon },
  { value: 'system', labelKey: 'layout.theme.system', icon: Monitor },
];

/** A three-way theme switch, exposed as a labelled radio group for keyboard and AT users. */
export function ThemeToggle({ className }: { className?: string | undefined }): ReactNode {
  const { preference, setPreference } = useTheme();
  const { t } = useTranslation();
  return (
    <div
      role="radiogroup"
      aria-label={t('layout.theme.colourThemeAriaLabel')}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg border border-border p-0.5',
        className,
      )}
    >
      {OPTIONS.map(({ value, labelKey, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={preference === value}
          aria-label={t(labelKey)}
          title={t(labelKey)}
          onClick={() => {
            setPreference(value);
          }}
          className={cn(
            'flex size-7 items-center justify-center rounded-md transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            preference === value
              ? 'bg-accent text-accent-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
